import { useMutation } from '@apollo/client'
import { useSearchParams } from 'react-router'
import { FLEET_URL, PLANNER_URL } from '@/utils/env'
import { useCallback } from 'react'
import client from '@/apollo'
import { useOnErrorAuto } from '@/utils/hooks'

import type { LoginMutation } from '../types.graphql'
import { LOGIN_MUTATION, LOGIN_WITH_MICROSOFT_MUTATION } from '../mutations'
import { setLoginToken, writeAuthCookie } from '../util'

type UseLoginProps = {
  /**
   * Selects the login variant, which determines the authentication flow
   * and the GraphQL mutation document used for the request.
   */
  variant: 'microsoft' | 'standard'

  // On successful login, whether to remember the username in localStorage
  rememberMe?: boolean

  // Optional callback for handling login errors
  onErrorCallback?: () => void
}

/**
 * Custom React hook for handling user login logic, including authentication,
 * localStorage management for "remember me" functionality, and post-login redirection.
 */
const useLogin = (props: UseLoginProps) => {
  const { variant, rememberMe = false, onErrorCallback } = props

  const mutationDocument =
    variant === 'microsoft' ? LOGIN_WITH_MICROSOFT_MUTATION : LOGIN_MUTATION

  const onErrorAuto = useOnErrorAuto()
  const [searchParams] = useSearchParams()

  /**
   * Updates the "rememberMe" state in localStorage based on the user's preference.
   *
   * If `rememberMe` is `false`, removes the "rememberMe" item from localStorage.
   * If `rememberMe` is `true`, stores the "rememberMe" flag and the provided username in localStorage.
   */
  const updateRememberMe = useCallback(
    (rememberMe: Required<UseLoginProps['rememberMe']>, username: string) => {
      if (!rememberMe) {
        localStorage.removeItem('rememberMe')
        return
      }

      localStorage.setItem(
        'rememberMe',
        JSON.stringify({
          rememberMe: rememberMe,
          username: username,
        })
      )
    },
    []
  )

  /**
   * Callback function to handle the completion of the login mutation.
   *
   * 1. Check if login was successful; if not, call error callback.
   * 2. Retrieve the authentication token from the login response.
   * 3. Determine the most recent company and desired redirect destination from query parameters.
   * 4. Update "remember me" state in localStorage with the username.
   * 5. If a redirect is specified and a most recent company exists, write the auth cookie and navigate.
   * 6. If no redirect is needed, reset the Apollo client store and update "remember me" state.
   */
  const onCompleted = useCallback(
    (data: LoginMutation) => {
      const ok = data.login.ok

      // If login failed, invoke the error callback and exit
      if (!ok) {
        onErrorCallback && onErrorCallback()
        return
      }

      const token = data.login.token
      setLoginToken(token)

      // TODO should we have another way of handling which company to redirect to?
      const mostRecentCompany = data.login.user.mostRecentCompany
      const redirectParam = searchParams.get('redirect')
      const redirectTo =
        redirectParam === 'fleet'
          ? FLEET_URL
          : redirectParam === 'planner'
          ? PLANNER_URL
          : null

      const username = data.login.user.username

      // If there's a most recent company and a redirect destination, handle redirection
      if (!!mostRecentCompany && !!redirectTo) {
        updateRememberMe(rememberMe, username)

        writeAuthCookie(token, mostRecentCompany.shortName)
        window.location.href = redirectTo
        return
      }

      // If no redirection is needed, reset the Apollo client store
      void client.resetStore().then(() => {
        updateRememberMe(rememberMe, username)
      })
    },
    [onErrorCallback, rememberMe, searchParams, updateRememberMe]
  )

  // Login mutation
  const [login, { loading }] = useMutation<LoginMutation>(mutationDocument, {
    onCompleted,
    onError: onErrorAuto(),
  })

  /**
   * Handle Microsoft login by sending the access token to the backend.
   */
  const handleMicrosoftLogin = useCallback(
    (token: string) => {
      void login({
        context: {
          headers: {
            authorization: `Bearer ${token}`,
          },
        },
      })
    },
    [login]
  )

  /**
   * Handle standard login with username and password.
   * Sends credentials to the backend.
   */
  const handleStandardLogin = useCallback(
    async (username: string, password: string) => {
      return login({
        variables: {
          username: username.replace(/\s/g, ''),
          password: password,
        },
      })
    },
    [login]
  )

  return { loading, handleMicrosoftLogin, handleStandardLogin }
}

export default useLogin
